/* global L */
import XHRPromise from 'xhr-promise'
import Cookies from 'js-cookie'
import Helpers from '../../modules/Helpers'
import Map from '../../modules/Map'
import ResultsHeader from './Results.Header'
import ResultsLocation from './Results.Location'

const activeClass = 'results-map--active'
const loadingClass = 'results-map__loading'

const searchControl = document.getElementsByClassName('results-aside')[0]

class ResultsMap {
    constructor(getQS) {
        this.map = null
        this.activeFetch = -1
        this.listeners()
        this.getQS = getQS
        this.cottageIcon = L.icon({
            iconUrl: '/media/map/icon-home.png',
            iconSize: [33, 47],
            iconAnchor: [17, 46],
            popupAnchor: [0, -40],
        })
        this.mapState = document.getElementById('map').getAttribute('data-mapState') || ''
    }

    listeners() {
        new Helpers(document.getElementsByClassName('results-map__open')).eventListener(() => {
            this.open()
            this.update()
            this.raiseEvent()
        })

        new Helpers(document.getElementsByClassName('results-map__close')).eventListener(() => {
            this.close()
            this.raiseEvent()
        })
    }

    open() {
        new Helpers(document.getElementsByTagName('body')).addClass(activeClass)
        if (this.map === null) {
            this.init()
        }
    }

    close() {
        new Helpers(document.getElementsByTagName('body')).removeClass(activeClass)
        this.openPopup = null
    }

    init() {
        this.map = new Map('map', {
            center: [50.72, -3.53],
            zoom: 7,
        })
    }

    isVisible() {
        return new Helpers(document.getElementsByTagName('body')).hasClass(activeClass)
    }

    update() {
        // clear existing markers
        this.map.clearMarkers()

        // show loading overlay
        new Helpers(document.getElementsByClassName(loadingClass)).setAttributes({ 'aria-hidden': false })

        this.getMarkers().then((data) => {
            data.cottages.forEach((cottage) => {
                const marker = this.map.addMarker([cottage.lat, cottage.lng], {
                    icon: this.cottageIcon,
                    cottage,
                }, this.buildPopup)

                marker.on('popupopen', (popup) => {
                    const code = popup.target.options.cottage.code
                    const type = popup.target.options.cottage.type
                    const center = this.map.map.getCenter()
                    const zoom = this.map.map.getZoom()
                    this.mapState = `${type},${code},${center.lat},${center.lng},${zoom}`
                    this.raiseEvent()
                })

                marker.on('popupclose', () => {
                    this.mapState = ''
                    this.raiseEvent()
                })
            })

            
            this.map.clusterMarkers()
            if (!this.loadMapState()) {
                this.map.fitMarkers()
            }

            new Helpers(document.getElementsByClassName(loadingClass)).setAttributes({ 'aria-hidden': true })
        })
    }

    buildPopup(layer) {
        const multi = typeof Cookies.get('auth') !== 'undefined' 
        const cottage = layer.options.cottage
        const link = `/holiday-cottage/desc-${cottage.code}.html`
        const inShortlist = window.sl.IsCodeInShortlists(cottage.code)
        return `<div class="results-map__popup" data-code="${cottage.type + cottage.code}">
                    <img src="${cottage.image}"/>
                    <h3><a href="${link}">${cottage.name}</a></h3>
                    ${cottage.type === 'X' ? '' : `<p>${cottage.rent}</p>`}
                    <p>${cottage.accom}</p>
                    <p>${cottage.pets > 0 ? 'Dogs welcome' : 'Regret no pets'}</p>
                    <div class="results-map__popup-cta">
                        <a href="${link}">Read More</a>
                        <button class="results-map__favouite favourite-button" data-code="${cottage.code}" data-multi="${multi}"  data-active="${inShortlist}"></button>
                    <div>
                </div>`
    }

    loadMapState() {
        if (this.mapState === '') return false
        const [type, code, lat, lng, zoom] = this.mapState.split(',')
        this.map.map.setView([parseFloat(lat), parseFloat(lng)], parseInt(zoom, 10))
        // try to find a matching marker
        let markerFound = false
        this.map.markers.some((marker) => {
            if (marker.options.cottage.type === type && marker.options.cottage.code === code) {
                setTimeout(() => { marker.openPopup() }, 400)
                markerFound = true
                return true
            }
            return false
        })
        return markerFound
    }

    getMarkers() {
        return new Promise((resolve) => {
            // fetch querystring from search
            var qs = this.getQS()

            //map view needs nlm for search but isn't set if default distance is used
            if (!qs.includes('nlm') && qs.includes('view=map')) {
                let resultsLocation = new ResultsLocation()
                let defaultDistance = resultsLocation.getTownDefaultDistance();
                qs += `&nlm=${defaultDistance.dd}`
            }

            // if we're already running a fetch, abort it
            if (this.activeFetch !== -1) {
                this.activeFetch.getXHR().abort()
            }
            // run search for markers
            this.activeFetch = new XHRPromise()
            this.activeFetch.send({
                url: `/feeds/resultsFeed.aspx?type=forMap&${qs}`,
            })
                .then(response => (new window.DOMParser()).parseFromString(response.responseText, 'text/xml'))
                .then((data) => {
                    const location = data.getElementsByTagName('location')[0]
                    const cottagesXml = data.getElementsByTagName('cottages')[0]
                    const complexCottageXml = data.getElementsByTagName('complexCottages')[0]
                    let cottages = this.parseCottages(cottagesXml.getElementsByTagName('cottage')) || []
                    let cxCottages = this.parseCottages(complexCottageXml.getElementsByTagName('cottage')) || []

                    // filter complex cottages
                    // - remove duplicate cottages in complexCottages resultset 
                    // - (duplicates can be present due to year based variance)
                    cxCottages = Array.from(new Set(cxCottages.map(c => c.code)))
                        .map(code => cxCottages.find(c => c.code === code))

                    // - those not already in the cottages resultset
                    cxCottages = cxCottages.filter((cx) => {
                        return cottages.map(c => c.code).includes(cx.code) === false
                    })

                    // merge the cottages
                    cottages = cottages.concat(cxCottages)

                    this.activeFetch = -1
                    resolve({
                        location,
                        cottages,
                    })
                }).catch((error) => {
                    // TODO: UI/UX for error handling
                    if (error.reason !== 'abort') {
                        console.log(error)
                    }
                })
        })
    }

    parseCottages(xmlCottages) {
        const cottages = []
        Array.from(xmlCottages).forEach((cottage) => {
            cottages.push({
                lat: parseFloat(cottage.getAttribute('lat')),
                lng: parseFloat(cottage.getAttribute('lon')),
                name: cottage.getAttribute('name'),
                town: cottage.getAttribute('town'),
                towndis: cottage.getAttribute('towndis'),
                type: cottage.getAttribute('type'),
                year: cottage.getAttribute('year'),
                code: cottage.getAttribute('code'),
                complexCode: cottage.getAttribute('xcode'),
                rent: cottage.getAttribute('rent'),
                accom: cottage.getAttribute('accom'),
                pets: cottage.getAttribute('maxPets'),
                image: cottage.getAttribute('picURL')
            })
        })
        return cottages
    }

    getNonDefaultValues() {
        const params = {}
        if (this.isVisible()) {
            params.view = 'map'

            if (this.mapState !== '') {
                params.mapState = this.mapState
            }
        }
        return params
    }

    getValues() {
        return this.getNonDefaultValues()
    }

    setValues(params) {
        this.mapState = params.mapState || ''

        if (params.view === 'map') {
            this.open()
            this.update()
        } else {
            this.close()
        }
        return this
    }

    raiseEvent() {
        searchControl.dispatchEvent(new CustomEvent('search-map'))

        return this
    }
}

export default ResultsMap
